import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';

import apiCalls from '../../utils/apiCalls';
import utils from '../../utils/utils';

import GeneralForm from '../../components/generalForm';
import { connect } from 'react-redux';
import RichText from '../../components/customFields/richText';
import ALLERGEN from '../../components/constants/allergen';
import MultiSelect from '../../components/customFields/multiSelect';
import CustomSelect from '../../components/customFields/customSelect';
import Helmet from 'react-helmet';
import UserLinkField from '../../components/customFields/userLinkField';

const RecipeForm = ({ match, action, intl, history }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [recipe, setRecipe] = useState({
    products: [],
  });
  const [defaultItemOptions, setDefaultItemOptions] = useState([]);

  const [chefs, setChefs] = useState([]);
  const [itemCollections, setItemCollections] = useState([]);

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (action === 'edit') {
      setError(null);
      setLoading(true);
      apiCalls
        .getRecipes({
          recipeId: match.params.recipeId,
        })
        .then((result) => {
          setRecipe(transformRecipeForForm(result.data.recipes[0]));
          setImage(result.data.recipes[0].image);
          setDefaultItemOptions(
            result.data.recipes[0].products.map((item) => ({
              value: item.id,
              label: `${utils.getItemName(item, formatMessage)} - #${item.id}`,
            }))
          );
          setLoading(false);
          getChefs();
        })
        .catch((errors) => {
          setError(utils.getErrorMessageFromResponse(errors));
          setLoading(false);
        });
    } else {
      getChefs();
    }
  }, [match.params.recipeId]);

  function getChefs() {
    apiCalls
      .getChefs()
      .then((result) => {
        setChefs(result.data.chefs);
        getItemCollections();
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
      });
  }

  function getItemCollections() {
    apiCalls
      .getItemCollections({ type: 'RECIPE' })
      .then((result) => {
        setItemCollections(result.data.itemCollections);
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
      });
  }

  const allergenOptions = Object.keys(ALLERGEN).map((key) => {
    const type = ALLERGEN[key];
    return {
      value: type,
      label: intl.formatMessage({ id: `recipe.form.field.allergen.${type}` }),
    };
  });

  const chefOptions = chefs.map((chef) => {
    return {
      value: chef.id,
      label: `#${chef.id} ${chef.name}`,
    };
  });

  const itemsOptions = itemCollections.map((ic) => {
    return {
      value: ic.id,
      label: `#${ic.id} ${ic.name}`,
    };
  });

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      userLink: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.url.label' }),
      },
      chefId: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.chef.id.label' }),
        options: chefOptions,
      },
      products: {
        type: 'array',
        title: intl.formatMessage({ id: 'recipe.form.field.products.label' }),
        async: true,
        loadOptions: (query, selectedItems) => {
          return apiCalls.getItems({ itemIds: selectedItems.join(','), query, admin: true }).then((response) =>
            response.data.items.map((item) => {
              return {
                value: item.id,
                label: `${utils.getItemName(item, formatMessage)} - #${item.id}`,
              };
            })
          );
        },
        defaultOptions: defaultItemOptions,
      },
    },
  };

  if (recipe.id || recipe.isCustom) {
    formSchema.properties = {
      ...formSchema.properties,
      name: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.name.label' }),
      },
      description: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.description.label' }),
      },
    };
  }

  formSchema.properties = {
    ...formSchema.properties,
    isCustom: {
      type: 'boolean',
      title: intl.formatMessage({ id: 'recipe.form.field.is.custom.label' }),
    },
    isPrivate: {
      type: 'boolean',
      title: intl.formatMessage({ id: 'recipe.form.field.is.private.id.label' }),
    },
  };

  if (recipe.isCustom) {
    formSchema.properties = {
      ...formSchema.properties,
      prepTime: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.prep.time.label' }),
      },
      peopleCount: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.people.count.label' }),
      },
      ingredients: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.ingredients.label' }),
      },
      cutlery: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.cutlery.label' }),
      },
      allergens: {
        type: 'array',
        title: intl.formatMessage({ id: 'recipe.form.field.allergens.label' }),
        options: allergenOptions,
      },
      itemsId: {
        type: 'string',
        title: intl.formatMessage({ id: 'recipe.form.field.items.id.label' }),
        options: itemsOptions,
        showClearOption: true,
      },
    };
  }

  const uiSchema = {
    userLink: {
      'ui:field': 'userLink',
    },
    description: {
      'ui:field': 'richText',
    },
    allergens: {
      'ui:field': 'multiSelect',
    },
    chefId: {
      'ui:field': 'customSelect',
    },
    itemsId: {
      'ui:field': 'customSelect',
    },
    products: {
      'ui:field': 'multiSelect',
    },
  };

  function onFormSubmit() {
    setLoading(true);

    if (action === 'edit') {
      return apiCalls
        .editRecipe({ recipe, image })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: '/admin/recipes',
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    } else {
      return apiCalls
        .createRecipe({ recipe, image })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: '/admin/recipes',
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    }
  }

  const buttonCopy = action === 'edit' ? 'recipe.edit.form.submit' : 'recipe.create.form.submit';

  const formFields = {
    richText: RichText,
    multiSelect: MultiSelect,
    customSelect: CustomSelect,
    userLink: UserLinkField,
  };

  const header =
    action === 'edit' && !loading
      ? intl.formatMessage({ id: 'recipe.edit.header' }, { recipe: recipe.id })
      : intl.formatMessage({ id: 'recipe.create.header' });

  return (
    <div className="row">
      <Helmet>
        <title>{header}</title>
      </Helmet>
      <div className="col-12 col-lg-10 offset-lg-1 admin__form">
        <GeneralForm
          title={header}
          onChange={(event) => setRecipe(event.formData)}
          onSubmit={onFormSubmit}
          uploadImage={(image) => apiCalls.uploadImage(image, 'recipe', recipe.id)}
          deleteImage={(image) => apiCalls.deleteImage(image)}
          onImagesChange={(images) => setImage(images[0])}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          uploadedImages={image ? [image] : []}
          formData={recipe}
          buttonCopy={buttonCopy}
          errorMessage={error}
          loading={loading}
          singleImage={true}
        />
      </div>
    </div>
  );
};

function transformRecipeForForm(recipe) {
  let result = {
    ...recipe,
    isCustom: !!recipe.prepTime || !!recipe.ingredients.length,
    chefId: recipe.chef.id,
    products: recipe.products.map((p) => p.id),
  };

  if (result.isCustom) {
    result = {
      ...result,
      prepTime: `${recipe.prepTime}`,
      peopleCount: `${recipe.peopleCount}`,
      ingredients: `${recipe.ingredients?.join(', ')}`,
      cutlery: `${recipe.cutlery?.join(', ')}`,
      labels: recipe.labels?.length ? `${recipe.labels.join(', ')}` : '',
      itemsId: recipe.items ? recipe.items.id : null,
    };
  }

  return result;
}

RecipeForm.propTypes = {
  currentUser: PropTypes.object,
  action: PropTypes.string.isRequired,
};

RecipeForm.defaultProps = {
  action: 'create',
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default injectIntl(connect(mapStateToProps)(RecipeForm));
